import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNauka, LIMIT_DEMO } from '../hooks/useNauka';
import { PRZEDMIOT_PO_SLUGU, LITERY, LICENCJA_PO_SLUGU, kluczeOdpowiedzi } from '../data/licencje';
import { pobierzPrzedmiot } from '../data/pytania';
import { odmien } from '../lib/odmiana';
import MarkdownRenderer from './MarkdownRenderer';
import JsonLd from '../seo/JsonLd';
import { breadcrumbJsonLd } from '../seo/siteMeta';
import styles from '../css/modules/Nauka.module.css';

/**
 * Tryb nauki: pytania z jednego działu, z natychmiastową informacją zwrotną.
 *
 * W odróżnieniu od symulatora egzaminu poprawna odpowiedź, wyjaśnienie i
 * podstawa prawna pokazują się od razu po kliknięciu - tu chodzi o naukę,
 * a nie o sprawdzenie się w warunkach egzaminacyjnych.
 */
const StudyMode = () => {
  const { licencja: licencjaSlug, przedmiot: przedmiotSlug } = useParams();
  const licencja = LICENCJA_PO_SLUGU[licencjaSlug];
  const przedmiot = PRZEDMIOT_PO_SLUGU[przedmiotSlug];

  const { questions, error, loading, demo } = useNauka({ licencja: licencjaSlug, przedmiot: przedmiotSlug });

  const [indeks, setIndeks] = useState(0);
  const [wybrana, setWybrana] = useState(null);
  const [dobrze, setDobrze] = useState(0);
  // Pełna lista pytań przedmiotu - niezależna od limitu demo, bo strony
  // /pytania/<slug> są publiczne i darmowe. To jest spis treści działu.
  const [spis, setSpis] = useState([]);

  useEffect(() => {
    setIndeks(0);
    setWybrana(null);
    setDobrze(0);
  }, [licencjaSlug, przedmiotSlug]);

  useEffect(() => {
    let anulowane = false;
    // Chunk przedmiotu jest już pobrany przez useNauka, więc lista nic nie
    // kosztuje w transferze. Rozwiązuje za to najdroższy problem SEO serwisu:
    // do 267 stron /pytania/* nie prowadził wcześniej ani jeden link - robot
    // znajdował je wyłącznie przez sitemapę i pobierał po jednej.
    pobierzPrzedmiot(przedmiotSlug)
      .then(wszystkie => {
        if (anulowane) return;
        setSpis(wszystkie.filter(p => (p.licencje || []).includes(licencjaSlug)));
      })
      .catch(() => !anulowane && setSpis([]));
    return () => {
      anulowane = true;
    };
  }, [licencjaSlug, przedmiotSlug]);

  const zacznijOdNowa = () => {
    setIndeks(0);
    setWybrana(null);
    setDobrze(0);
  };

  if (!licencja || !przedmiot) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.h1}>Nie znamy takiego przedmiotu</h1>
        <Link className={styles.cta} to="/">Wróć na stronę główną</Link>
      </div>
    );
  }

  if (loading) {
    return <div className={styles.wrap}><p className={styles.info}>Wczytujemy pytania…</p></div>;
  }

  if (error || !questions.length) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.h1}>{przedmiot.nazwa}</h1>
        <p className={styles.info}>{error || 'Dla tego uprawnienia nie mamy jeszcze pytań z tego przedmiotu.'}</p>
        <Link className={styles.cta} to={`/nauka/${licencja.slug}`}>Wybierz inny przedmiot</Link>
      </div>
    );
  }

  const pytanie = questions[indeks];
  const klucze = kluczeOdpowiedzi(pytanie);
  const sprawdzone = wybrana !== null;
  const koniec = indeks >= questions.length - 1;

  const zaznacz = key => {
    if (sprawdzone) return;
    setWybrana(key);
    if (key === pytanie.correctAnswer) setDobrze(d => d + 1);
  };

  const dalej = () => {
    setWybrana(null);
    setIndeks(i => Math.min(questions.length - 1, i + 1));
  };

  return (
    <div className={styles.wrap}>
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        { name: licencja.nazwa, path: licencja.path },
        { name: 'Nauka', path: `/nauka/${licencja.slug}` },
        { name: przedmiot.nazwa, path: `/nauka/${licencja.slug}/${przedmiot.slug}` },
      ])} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link> / <Link to={licencja.path}>{licencja.nazwa}</Link>
        {' / '}<Link to={`/nauka/${licencja.slug}`}>Nauka</Link> / {przedmiot.nazwa}
      </nav>

      <h1 className={styles.h1}>{przedmiot.nazwa}</h1>
      <p className={styles.lead}>{przedmiot.opis}</p>

      <div className={styles.postep}>
        <span>Pytanie {indeks + 1} z {questions.length}</span>
        <span>Poprawne: {dobrze}</span>
        {demo && <span className={styles.demoTag}>wersja demo</span>}
      </div>

      <div className={styles.karta}>
        <h2 className={styles.pytanie}>{pytanie.question}</h2>

        <div className={styles.odpowiedzi}>
          {klucze.map(k => {
            const kl = [styles.odpowiedz];
            if (sprawdzone && k === pytanie.correctAnswer) kl.push(styles.dobra);
            else if (sprawdzone && k === wybrana) kl.push(styles.zla);
            else if (k === wybrana) kl.push(styles.wybrana);
            return (
              <button key={k} type="button" className={kl.join(' ')} onClick={() => zaznacz(k)} disabled={sprawdzone}>
                <span className={styles.litera}>{LITERY[k]}</span>
                <span>{pytanie[k]}</span>
              </button>
            );
          })}
        </div>

        {sprawdzone && (
          <div className={styles.omowienie}>
            <p className={wybrana === pytanie.correctAnswer ? styles.werdyktOk : styles.werdyktZle}>
              {wybrana === pytanie.correctAnswer ? 'Dobrze' : `Poprawna odpowiedź: ${LITERY[pytanie.correctAnswer]}`}
            </p>
            {pytanie.explanation && <MarkdownRenderer content={pytanie.explanation} />}
            {pytanie.legal_basis && <p className={styles.podstawa}>Podstawa: {pytanie.legal_basis}</p>}
            <Link className={styles.link} to={`/pytania/${pytanie.slug}`}>Otwórz osobną stronę tego pytania</Link>
          </div>
        )}
      </div>

      <div className={styles.akcje}>
        {sprawdzone && !koniec && (
          <button type="button" className={styles.cta} onClick={dalej}>Następne pytanie</button>
        )}
        {sprawdzone && koniec && (
          <>
            <p className={styles.info}>
              To ostatnie pytanie z tej porcji. Poprawnie: {dobrze} z {questions.length}{' '}
              {odmien(questions.length, ['pytania', 'pytań', 'pytań'])}.
            </p>
            {/* Reset stanu zamiast window.location.reload() - przeładowanie
                całej aplikacji po to, żeby wyzerować trzy liczniki, kosztowało
                ponowne pobranie bundle'a. */}
            <button type="button" className={styles.cta} onClick={zacznijOdNowa}>
              Zacznij od nowa
            </button>
          </>
        )}
        <Link className={styles.ctaDrugi} to={`/nauka/${licencja.slug}`}>Inne przedmioty</Link>
      </div>

      {spis.length > 0 && (
        <div className={styles.tresc}>
          <h2 className={styles.h2}>
            Wszystkie pytania z tego przedmiotu ({spis.length})
          </h2>
          <p className={styles.info}>
            Każde pytanie ma osobną stronę z odpowiedzią, omówieniem i podstawą prawną.
          </p>
          <ul className={styles.listaPokrewnych}>
            {spis.map(p => (
              <li key={p.slug}>
                <Link to={`/pytania/${p.slug}`}>{p.question}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {demo && (
        <p className={styles.demoInfo}>
          W wersji demo pokazujemy pierwsze {LIMIT_DEMO} pytań z tego przedmiotu.{' '}
          <Link to="/cennik">Pełny dostęp</Link> otwiera całą bazę oraz pojedynek na pytaniach
          losowanych z całego materiału.
        </p>
      )}
    </div>
  );
};

export default StudyMode;
